import { z } from 'zod'
import { FORM_ID_ALPHABET, FORM_ID_PATTERN, FORM_LANGS, FORM_SLUG_PATTERN } from './form-ids'

/**
 * The normalized form document Drupal exports to R2 (`<ns>/forms/<lang>/<type>/<slug>.json`).
 * Values arrive resolved: a campaign already carries what it inherits from its standard. A reader
 * that meets another `schemaVersion` treats the form as missing. See ../README.md.
 */
export const FORM_SCHEMA_VERSION = 1

export { FORM_ID_ALPHABET, FORM_ID_PATTERN, FORM_LANGS, FORM_SLUG_PATTERN }

// Reader schemas use zod's default z.object, which drops unknown keys. Never .strict(): a newer
// writer adds keys, and an older reader must still parse the document (README, Reader rules).
const zSlug = z.string().regex(FORM_SLUG_PATTERN)
const zFormId = z.string().regex(FORM_ID_PATTERN)
const zUpdatedAt = z.iso.datetime({ offset: true })
/** A Contentful thank-you entry id, until result pages move. */
const zResultId = z.string().min(1).nullable()

export const zFormRowV1 = z.object({
  eyebrow: z.string(),
  heading: z.string(),
  /**
   * Rich text: HTML limited to p, br, strong, em, a (https or mailto href), ul, ol and li; readers
   * strip anything else. A top-level list becomes the row's check-icon bullets.
   */
  body: z.string(),
  /** Cloudinary public id, no version or extension. */
  image: z.string(),
  /** Plain text; null when none is set, and readers use the heading. Optional for older writers. */
  imageAlt: z.string().nullable().optional(),
  link: z.string(),
  button: z.string(),
})

/**
 * A thank-you page a form carries itself. `heading` greets with `{fname}`, the donor's first name;
 * `lead` and `body` are rich text with the row allowlist. See ../README.md, "Thank-you pages".
 */
export const zFormThanksV1 = z.object({
  kicker: z.string(),
  heading: z.string(),
  lead: z.string(),
  body: z.string(),
})

// The keys every amount form shares, `type` aside; each type's schema adds its own.
const formFieldsV1 = {
  schemaVersion: z.literal(FORM_SCHEMA_VERSION),
  id: zFormId,
  slug: zSlug,
  lang: z.enum(FORM_LANGS),
  org: z.enum(['djurensratt', 'project1882']),
  variant: z.enum(['standard', 'campaign']),
  /** ISO 4217. Amounts are whole units of it; never derive it from `lang`. */
  currency: z.string().regex(/^[A-Z]{3}$/),
  /** Feature tokens a reader must understand; a reader that doesn't treats the form as missing. */
  requires: z.array(z.string()),
  standardId: zFormId.nullable(),
  heading: z.string(),
  eyebrow: z.string(),
  /** Rich text, the same allowlist as a row's `body`; lists stay in place here. */
  lead: z.string(),
  /** Cloudinary public id, no version or extension. */
  heroImage: z.string(),
  /** Plain text; null when none is set, and readers use the heading. Optional for older writers. */
  heroImageAlt: z.string().nullable().optional(),
  rows: z.array(zFormRowV1),
  /** In display order; readers never sort. */
  presets: z.array(z.number().int().positive()),
  defaultAmount: z.number().int().positive().nullable(),
  minimum: z.number().int().positive().nullable(),
  /** Open list: readers keep the methods they know and drop the rest. */
  methods: z.array(z.string()),
  code: z.string(),
  /** HTML. */
  consent: z.string(),
  closed: z.boolean(),
  noindex: z.boolean(),
  /** After a paid gift; null or absent: `results.done` names a Contentful entry. */
  thanks: zFormThanksV1.nullable().optional(),
  /**
   * After a gift the donor completes later: inbetalningskort, paper consent, a payment link by
   * SMS or email, or an invoice. Null or absent: `results.pending`, then `thanks`.
   */
  pending: zFormThanksV1.nullable().optional(),
  /** After a payment that was aborted; null or absent: `results.aborted`. */
  aborted: zFormThanksV1.nullable().optional(),
  /**
   * Second asks in display order. Each item is read by its `type` (fundraising-drupal `knownAsks`),
   * so an ask a reader doesn't know never invalidates the form.
   */
  next: z.array(z.unknown()).optional(),
  results: z.object({ done: zResultId, pending: zResultId, aborted: zResultId }),
  updatedAt: zUpdatedAt,
}

const namesItsStandard = (form: { variant: string; standardId: string | null }) =>
  (form.variant === 'campaign') === (form.standardId !== null)
const standardRule = {
  message: 'a campaign names its standard, a standard names none',
  path: ['standardId'],
}

export const zFormDocumentV1 = z
  .object({
    type: z.literal('donation'),
    ...formFieldsV1,
    /** Present only on a Företagsgåva; it also carries `requires: ['payer']`, so a reader that predates it refuses. */
    payer: z.literal('company').optional(),
  })
  .refine(namesItsStandard, standardRule)

/** A Premie a monthly donor picks from (dr_forms `Presents::contract`). */
export const zFormPresentV1 = z.object({
  /** English machine key, minted once; never a CRM code. */
  key: z.string().min(1),
  /** What donors see. */
  name: z.string(),
  /** Cloudinary public id, no version or extension; '' for none. */
  image: z.string(),
  imageAlt: z.string().nullable().optional(),
  description: z.string(),
  /** The present's code per CRM: Mysoft's is the membership's Premieval. Open: a new CRM adds a key. */
  codes: z.object({ mysoft: z.string() }),
})

/**
 * A Månadsgivare form (`<ns>/forms/<lang>/monthly/<slug>.json`): a donation form's keys with
 * `presents`, kr/mån amounts, methods `autogiro` and `paper`, and a värvningskod as `code`.
 * `pending` is the paper-consent page and `aborted` the aborted e-signing page.
 */
export const zMonthlyDocumentV1 = z
  .object({ type: z.literal('monthly'), ...formFieldsV1, presents: z.array(zFormPresentV1) })
  .refine(namesItsStandard, standardRule)

/** `ordinary` first: the order Drupal exports them in. */
export const MEMBERSHIP_CATEGORIES = ['ordinary', 'reduced', 'youth'] as const

const zIsoDate = z.iso.date()

/** A motif a buyer picks for a gift card (dr_forms `Motifs::contract`); only motifs with an image are exported. */
export const zFormMotifV1 = z.object({
  /** English machine key, minted once; never a CRM code. */
  key: z.string().min(1),
  name: z.string(),
  description: z.string(),
  /** Cloudinary public id, no version or extension. */
  image: z.string().min(1),
  imageAlt: z.string().nullable(),
  /** The motif's code per CRM; '' while none is set. */
  codes: z.object({ mysoft: z.string() }),
})

// Priced by category on the server, so the amount keys are present but empty.
const membershipFields = {
  ...formFieldsV1,
  presets: z.array(z.never()),
  defaultAmount: z.null(),
  minimum: z.null(),
  categories: z.array(z.enum(MEMBERSHIP_CATEGORIES)).nonempty(),
}

// What a Minnesgåva adds: motifs only. Act sends it today, so it has no delivery cut-off.
const motifFields = { motifs: z.array(zFormMotifV1) }

// What a gift card adds: motifs, and a delivery cut-off as `YYYY-MM-DD` and a bare noun ("julafton").
const giftCardFields = {
  ...motifFields,
  lastOrderDate: zIsoDate.nullable(),
  lastOrderOccasion: z.string().nullable(),
}

/** An Årsmedlem form (`<ns>/forms/<lang>/membership/<slug>.json`). */
export const zMembershipDocumentV1 = z
  .object({ type: z.literal('membership'), ...membershipFields })
  .refine(namesItsStandard, standardRule)

/** A Gåvomedlemskap form (`.../giftmembership/<slug>.json`): a membership with presents, motifs and dates. */
export const zGiftMembershipDocumentV1 = z
  .object({
    type: z.literal('giftmembership'),
    ...membershipFields,
    presents: z.array(zFormPresentV1),
    ...giftCardFields,
    startDate: zIsoDate.nullable(),
    /** A path on raddadjuren.se/medlem, starting with `/`. */
    childPath: z.string().startsWith('/').nullable(),
  })
  .refine(namesItsStandard, standardRule)

/** A Gåvobevis (`.../giftdonation/<slug>.json`). */
export const zGiftDonationDocumentV1 = z
  .object({ type: z.literal('giftdonation'), ...formFieldsV1, ...giftCardFields })
  .refine(namesItsStandard, standardRule)

/** A Minnesgåva (`.../memorial/<slug>.json`). */
export const zMemorialDocumentV1 = z
  .object({ type: z.literal('memorial'), ...formFieldsV1, ...motifFields })
  .refine(namesItsStandard, standardRule)

/**
 * Every amount-form type Drupal exports, by `type`. A Företagsgåva is a `donation` with `payer`.
 * Petitions have their own schema (zPetitionDocumentV1).
 */
export const zAnyFormDocumentV1 = z.discriminatedUnion('type', [
  zFormDocumentV1,
  zMonthlyDocumentV1,
  zMembershipDocumentV1,
  zGiftMembershipDocumentV1,
  zGiftDonationDocumentV1,
  zMemorialDocumentV1,
])

/** Every document `type` the contract defines; a reader serves a subset and refuses the rest. */
export const FORM_DOCUMENT_TYPES = [
  'donation',
  'monthly',
  'petition',
  'membership',
  'giftmembership',
  'giftdonation',
  'memorial',
] as const
export type FormTypeV1 = (typeof FORM_DOCUMENT_TYPES)[number]

export type FormRowV1 = z.infer<typeof zFormRowV1>
export type FormDocumentV1 = z.infer<typeof zFormDocumentV1>
export type MonthlyDocumentV1 = z.infer<typeof zMonthlyDocumentV1>
export type MembershipDocumentV1 = z.infer<typeof zMembershipDocumentV1>
export type GiftMembershipDocumentV1 = z.infer<typeof zGiftMembershipDocumentV1>
export type GiftDonationDocumentV1 = z.infer<typeof zGiftDonationDocumentV1>
export type MemorialDocumentV1 = z.infer<typeof zMemorialDocumentV1>
export type FormMotifV1 = z.infer<typeof zFormMotifV1>
export type AnyFormDocumentV1 = z.infer<typeof zAnyFormDocumentV1>
export type FormPresentV1 = z.infer<typeof zFormPresentV1>
export type FormThanksV1 = z.infer<typeof zFormThanksV1>

/** `<ns>/forms/<lang>/<type>/_index.json`: the published forms of one type and language. */
export const zFormIndexV1 = z.object({
  schemaVersion: z.literal(FORM_SCHEMA_VERSION),
  forms: z.record(
    z.string(),
    z.object({
      id: zFormId,
      updatedAt: zUpdatedAt,
      /** Shown to donors from Drupal; false keeps them on Contentful unless opted in. Missing reads as true. */
      live: z.boolean().optional(),
    }),
  ),
})

export type FormIndexV1 = z.infer<typeof zFormIndexV1>

/** A Namninsamling or a Mejlprotest; readers refuse any other kind. */
export const PETITION_KINDS = ['signature', 'protest'] as const

/** A Firebase key: no `.`, `#`, `$`, `[`, `]` or `/`. */
const zFirebaseKey = z.string().regex(/^[^.#$[\]/]+$/)

/**
 * A petition translation Drupal exports to `<ns>/forms/<lang>/petition/<slug>.json`, listed in the
 * same `_index.json` shape as donation forms. Server-only: `protest` holds the recipients and the
 * SendGrid template and must never reach a browser. dr_forms README, "Namninsamling".
 */
const zPetitionFieldsV1 = z.object({
  schemaVersion: z.literal(FORM_SCHEMA_VERSION),
  type: z.literal('petition'),
  /** Open here so a reader can name an unknown kind when it refuses the document. */
  kind: z.string(),
  id: zFormId,
  lang: z.enum(FORM_LANGS),
  org: z.enum(['djurensratt', 'project1882']),
  slug: zSlug,
  heading: z.string(),
  /** Rich text, the row allowlist. */
  lead: z.string(),
  /** Cloudinary public id, no version or extension; '' for none. */
  heroImage: z.string(),
  heroImageAlt: z.string().nullable().optional(),
  /** HTML; the language's standard text when the petition has none of its own, '' when neither. */
  consent: z.string(),
  /** After a signature; null: the site's default thank-you page for the type. */
  thanks: zFormThanksV1.nullable().optional(),
  /** Asks after the signature, read by `type` as a donation form's `next` is. */
  next: z.array(z.unknown()).optional(),
  closed: z.boolean(),
  /** HTML. */
  closedMessage: z.string(),
  noindex: z.boolean(),
  /** The Kampanjkoder `petition` code a signature is registered on. */
  code: z.string(),
  /** Null for a Namninsamling. */
  protest: z.object({ recipients: z.array(z.string()), template: z.string() }).nullable(),
  /** Mailchimp tag, applied verbatim; '' for none. */
  marketingTag: z.string(),
  smsActive: z.boolean(),
  /** The Firebase key of the signature counter (Contentful's `dbOverride`, else its slug). */
  counterKey: zFirebaseKey,
  /** The Firebase key of the comments (Contentful's slug). */
  commentsKey: zFirebaseKey,
  /** Shown to signers from Drupal; the index entry's `live` says the same. */
  live: z.boolean(),
  updatedAt: zUpdatedAt,
})

// Drupal's first exports named the version `v`; read that as `schemaVersion` until none are left.
const legacyVersion = (value: unknown) =>
  value && typeof value === 'object' && 'v' in value && !('schemaVersion' in value)
    ? { ...value, schemaVersion: (value as { v: unknown }).v }
    : value

export const zPetitionDocumentV1 = z.preprocess(legacyVersion, zPetitionFieldsV1)

export type PetitionDocumentV1 = z.infer<typeof zPetitionFieldsV1>
